import {
  AiMessage,
  FullResourceFragment,
  PdfHighlightInput,
} from "~/graphql/codegen/graphql";
import { useMutation, useQuery, useSubscription } from "@apollo/client/react";
import {
  CREATE_ANNOTATION_HIGHLIGHT_MUTATION,
  EDIT_ANNOTATION_HIGHLIGHT_MUTATION,
  DELETE_HIGHLIGHT_MUTATION,
  ADD_SHARED_HIGHLIGHT_MUTATION,
  AI_ASK_MUTATION,
  AI_SUMMARISE_MUTATION,
  AI_SIMPLIFY_MUTATION,
  AI_TRANSLATE_MUTATION,
  AI_TRANSLATE_LANGUAGES_QUERY,
  SUBMIT_TRIVIA_ANSWER_MUTATION,
} from "../annotate-mutations";
import { graphql } from "~/graphql/codegen";

const AI_DELTA_SUBSCRIPTION = graphql(`
  subscription AiDeltaPopover($idempotencyID: ID!) {
    aiDelta(idempotencyID: $idempotencyID) {
      message {
        role
        content
      }
    }
  }
`);

import useAnnotationManager from "../hooks/annotation-manager";
import React, { useEffect, useMemo, useState } from "react";
import metrics from "~/utils/metrics";
import { mapHighlightGQLToIHighlight } from "../highlight-util";
import useAuthCheck from "~/hooks/check-authenticated";
import { useRevalidator } from "react-router";
import { isFocused } from "~/components/annotate/hooks/focus-mode";
import { useHighlightContext } from "./use-selected-highlight";
import {
  IHighlightCommon,
  IHtmlHighlightInput,
  InlineTriviaExtension,
  IPdfHighlightInput,
  PopoverConfiguration,
  PopoverConfigurationOptions,
  PopoverOptions,
} from "~/typings/annotate/annotation";
import { useCurrentSpace, useProfile, useUser } from "~/hooks/context-hook";
import { toast } from "@mindstonehq/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

const keys = ["marker0", "marker1", "marker2", "marker3", "marker4", "marker5"];
export function useAnnotatePopoverConfiguration(
  article: FullResourceFragment,
): PopoverConfiguration {
  const { t } = useLingui();
  const [comments, highlights] = useAnnotationManager(
    article.id,
    article.annotations,
  );
  const { openComments, openNote, openInvestigator } = useHighlightContext();
  const checkAuth = useAuthCheck();
  const { revalidate: refresh } = useRevalidator();
  const user = useProfile();

  // const isDarkMode = useDarkMode();
  const isDarkMode = false;
  const focusMode = isFocused();
  const [createHighlightMutation] = useMutation(
    CREATE_ANNOTATION_HIGHLIGHT_MUTATION,
  );
  const [editHighlightMutation] = useMutation(
    EDIT_ANNOTATION_HIGHLIGHT_MUTATION,
  );
  const [deleteHighlightMutation] = useMutation(DELETE_HIGHLIGHT_MUTATION);
  const [addSharedHighlight] = useMutation(ADD_SHARED_HIGHLIGHT_MUTATION);
  const [aiSummariseMutation] = useMutation(AI_SUMMARISE_MUTATION);
  const [aiSimplifyMutation] = useMutation(AI_SIMPLIFY_MUTATION);
  const [aiTranslateMutation] = useMutation(AI_TRANSLATE_MUTATION);
  const [askAIMutation] = useMutation(AI_ASK_MUTATION);
  const [submitTriviaMutation] = useMutation(SUBMIT_TRIVIA_ANSWER_MUTATION);
  const { data: aiTranslateLanguagesResp } = useQuery(
    AI_TRANSLATE_LANGUAGES_QUERY,
  );
  const currentSpace = useCurrentSpace();

  function onHighlightEdit(
    highlightInput: IHtmlHighlightInput | IPdfHighlightInput,
  ) {
    if (!article) return Promise.reject("article not defined");
    if (!highlightInput.id) {
      console.error("highlight ID is not defined");
      return Promise.reject("highlight ID not defined");
    }
    metrics.track("highlight_edit", {
      article_id: article.id,
      view: "annotate",
    });

    const { id, ...rest } = highlightInput;
    return editHighlightMutation({
      variables: {
        articleId: article.id,
        highlightId: id,
        htmlHighlightInput:
          highlightInput.__typename === "HTMLHighlight"
            ? (rest as IHtmlHighlightInput)
            : null,
        pdfHighlightInput:
          highlightInput.__typename === "PDFHighlight"
            ? (rest as PdfHighlightInput)
            : null,
      },
    })
      .then((res) => {
        refresh();
        if (!res.data) {
          // eslint-disable-next-line lingui/no-unlocalized-strings -- internal dev error not surfaced to user
          throw "data missing";
        }
        return mapHighlightGQLToIHighlight(res.data.editAnnotationHighlight);
      })
      .catch((error) => {
        console.error(error);
        toast.error(t(msg`We couldn't save this highlight. Please try again later.`));
        throw error; // Propagate
      });
  }

  function onHighlightAdd(
    highlightInput: IHtmlHighlightInput | IPdfHighlightInput,
  ): Promise<IHighlightCommon> {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to add a highlight`),
      `${location.href}`,
      "highlight_add",
    );
    if (!authenticated) {
      return Promise.reject();
    }
    metrics.track("highlight_create", {
      article_id: article.id,
      view: "annotate",
    });
    return createHighlightMutation({
      variables: {
        articleId: article.id,
        htmlHighlightInput:
          highlightInput.__typename === "HTMLHighlight"
            ? (highlightInput as IHtmlHighlightInput)
            : null,
        pdfHighlightInput:
          highlightInput.__typename === "PDFHighlight"
            ? (highlightInput as PdfHighlightInput)
            : null,
      },
    })
      .then((res) => {
        refresh();
        if (!res.data) {
          // eslint-disable-next-line lingui/no-unlocalized-strings -- internal dev error not surfaced to user
          throw "data missing";
        }
        return mapHighlightGQLToIHighlight(res.data.createAnnotationHighlight);
      })
      .catch((error) => {
        console.error(error);
        toast.error(t(msg`We couldn't save this highlight. Please try again later.`));
        throw error; // Propagate
      });
  }

  function onHighlightRemove(highlightID: string) {
    if (!article) return Promise.reject("article not defined");
    metrics.track("highlight_remove", {
      article_id: article.id,
      view: "annotate",
    });
    if (!highlightID) {
      console.error("highlight ID is not defined");
      return Promise.reject("highlight ID not defined");
    }
    return deleteHighlightMutation({
      variables: {
        articleId: article.id,
        id: highlightID,
      },
    })
      .then((res) => {
        refresh();
        //TODO: closePanel();
      })
      .catch((error) => {
        //TODO: closePanel();
        toast.error(
          t(msg`We couldn't remove this highlight. Please try again later.`),
        );
      });
  }

  const handleAddSharedHighlight = (text: string) => {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "highlight_image",
    );
    if (!authenticated) {
      return Promise.reject();
    }

    metrics.track("highlight_image_created");
    return addSharedHighlight({
      variables: {
        text: text,
        source: article.siteName,
        sourceUrl: article.url,
      },
    }).then((res) => {
      return Promise.resolve(res.data?.addSharedHighlight || null);
    });
  };

  const askAI = (
    selection: string,
    messages: AiMessage[],
    q: string,
    idempotencyID?: string,
  ) => {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "ask_ai",
    );
    if (!authenticated) {
      return Promise.reject();
    }
    metrics.track("ask_ai", {
      article_id: article.id,
      view: "annotate",
    });
    return askAIMutation({
      variables: {
        context: {
          articleID: article.id,
          mindspaceID: currentSpace.id,
          selection,
          messages,
        },
        query: q,
        idempotencyID,
      },
    }).then((resp) => resp.data?.aiAsk);
  };

  const aiSummarise = (
    selection: string,
    messages: AiMessage[],
    idempotencyID?: string,
  ) => {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "ai_summarise",
    );
    if (!authenticated) {
      return Promise.reject();
    }
    metrics.track("ai_summarise", {
      article_id: article.id,
      view: "annotate",
    });
    return aiSummariseMutation({
      variables: {
        context: {
          articleID: article.id,
          mindspaceID: currentSpace.id,
          selection,
          messages,
        },
        idempotencyID,
      },
    }).then((resp) => resp.data?.aiSummarise);
  };

  const aiSimplify = (
    selection: string,
    messages: AiMessage[],
    idempotencyID?: string,
  ) => {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "ai_simplify",
    );
    if (!authenticated) {
      return Promise.reject();
    }
    metrics.track("ai_simplify", {
      article_id: article.id,
      view: "annotate",
    });
    return aiSimplifyMutation({
      variables: {
        context: {
          articleID: article.id,
          mindspaceID: currentSpace.id,
          selection,
          messages: messages.length ? messages : null,
        },
        idempotencyID,
      },
    }).then((resp) => resp.data?.aiSimplify);
  };

  const aiTranslate = (
    selection: string,
    messages: AiMessage[],
    language: string,
    idempotencyID?: string,
  ) => {
    if (!article) return Promise.reject("article not defined");
    const authenticated = checkAuth(
      t(msg`You need to be logged in to use this feature`),
      `${location.href}`,
      "ai_translate",
    );
    if (!authenticated) {
      return Promise.reject();
    }
    metrics.track("ai_translate", {
      article_id: article.id,
      view: "annotate",
    });
    return aiTranslateMutation({
      variables: {
        context: {
          articleID: article.id,
          mindspaceID: currentSpace.id,
          selection,
          messages,
        },
        language,
        idempotencyID,
      },
    }).then((resp) => resp.data?.aiTranslate);
  };

  const useAIDelta = (idempotencyID: string) => {
    const [delta, setDelta] = useState("");

    useEffect(() => {
      // Reset when the ID changes
      if (idempotencyID) {
        setDelta("");
      }
    }, [idempotencyID]);

    useSubscription(AI_DELTA_SUBSCRIPTION, {
      variables: {
        idempotencyID,
      },
      skip: idempotencyID == "",
      onData: (res) => {
        const m = res.data.data?.aiDelta.message;
        if (!m) {
          console.error("AI Message:", res.data.error);
          return;
        }
        setDelta((prev) => prev + m.content);
      },
    });

    return delta;
  };

  const popoverOptions: PopoverOptions = React.useMemo(() => {
    const popoverOptions: PopoverOptions = [
      {
        type: PopoverConfigurationOptions.Copy,
      },
      {
        type: PopoverConfigurationOptions.Highlighters,
        highlightEdit: onHighlightEdit,
        highlightAdd: onHighlightAdd,
        highlightRemove: onHighlightRemove,
      },
      {
        type: PopoverConfigurationOptions.AskAI,
        useAIDelta,
        askAI,
        aiSummarise,
        aiSimplify,
        aiTranslate,
        aiTranslateLanguages: aiTranslateLanguagesResp?.aiTranslateLanguages || [
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "English",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Korean",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Chinese",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Japanese",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Spanish",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Russian",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "French",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Portuguese",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "German",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Italian",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Dutch",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Indonesian",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Filipino",
          // eslint-disable-next-line lingui/no-unlocalized-strings -- API contract: sent verbatim as `language` to aiTranslate mutation; must match backend-supplied list
          "Vietnamese",
        ],
      },
      {
        type: PopoverConfigurationOptions.Notes,
        noteClicked: openNote,
      },

      {
        type: PopoverConfigurationOptions.Comments,
        commentsClicked: openComments,
      },
      {
        type: PopoverConfigurationOptions.TweetHighlight,
        addSharedHighlight: handleAddSharedHighlight,
      },
    ];
    return popoverOptions;
  }, [!aiTranslateLanguagesResp?.aiTranslateLanguages]);

  const extensions = useMemo(() => {
    return article.extensions
      .map((x) => {
        switch (x.__typename) {
          case "TriviaExtension":
            return {
              ...x,
              submit: (questionId, answerId) => {
                return submitTriviaMutation({
                  variables: {
                    articleID: article.id,
                    answerID: answerId,
                    questionID: questionId,
                  },
                })
                  .then((res) => {
                    if (!res.data) {
                      // eslint-disable-next-line lingui/no-unlocalized-strings -- caught internally; not surfaced as toast
                      throw "Something went wrong, please try again later";
                    }
                    return res.data.submitTriviaAnswer;
                  })
                  .catch((err) => {
                    // eslint-disable-next-line lingui/no-unlocalized-strings -- caught internally; not surfaced as toast
                    throw "Something went wrong, please try again later.";
                  });
              },
            } as InlineTriviaExtension;
        }
        return null;
      })
      .filter((x) => !!x);
  }, [article.extensions, article.id]);

  return useMemo(
    () =>
      ({
        darkMode: isDarkMode,
        extensions: extensions,
        mentionsEnabled: true,
        options: popoverOptions,

        currentUser: {
          id: user?.id || "",
          displayName: user?.displayName || "", // TODO: Fix this
          profilePictureURL: user?.profilePictureURL || "", //TODO: We know it's set, bad typing on the me query.
          username: user?.username,
        },

        focusMode,
        addSharedHighlight: handleAddSharedHighlight,
      }) as PopoverConfiguration,
    [
      user,
      isDarkMode,
      focusMode,
      extensions,
      !aiTranslateLanguagesResp?.aiTranslateLanguages,
    ],
  );
}
